<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { toRoute } from '@/helpers/route'
import type { Tab } from '@/types'

const emit = defineEmits<{
  (e: 'update:modelValue', tabKey: Tab['key']): void
}>()
const props = defineProps({
  tabs: {
    type: Array as PropType<Tab[]>,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, Symbol] as PropType<Tab['key']>,
    default: null,
  },
})

const valueProxy = computed<Tab['key']>({
  get: () => props.modelValue,
  set: (tabKey) => emit('update:modelValue', tabKey),
})
</script>
<template>
  <div class="tw-flex tw-w-full tw-items-center tw-border-b">
    <div class="tw-flex tw-w-full tw-items-center tw-space-x-12">
      <template
        v-for="tab in props.tabs"
        :key="tab.key"
      >
        <router-link
          v-if="tab.route"
          v-slot="{ href, navigate, isExactActive }"
          custom
          :to="toRoute(tab.route)"
        >
          <a
            v-tooltip="tab.tooltip"
            :href="href"
            :class="[
              isExactActive || tab.key == modelValue
                ? 'tw-border-primary tw-text-black'
                : 'tw-border-transparent tw-text-grey hover:tw-text-black',
            ]"
            class="tw-relative tw--mb-px tw-flex tw-h-12 tw-items-center tw-border-b-3 tw-text-xs tw-font-medium tw-uppercase"
            data-test-role="tab"
            @click="navigate"
          >
            <span class="tw-hidden tablet:tw-inline-block">
              {{ tab.label }}
            </span>
            <span class="tablet:tw-hidden">
              {{ tab.shortLabel || tab.label }}
            </span>
            <span
              v-if="tab.count && tab.count > 0"
              class="tw-shadow-solid tw-ml-3 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue tw-text-xs tw-text-white"
            >
              {{ tab.count }}
            </span>
          </a>
        </router-link>
        <div
          v-else
          :class="[
            tab.key === modelValue
              ? 'tw-border-primary tw-text-black'
              : 'tw-border-transparent tw-text-grey hover:tw-text-black',
          ]"
          class="tw--mb-px tw-border-b-3"
        >
          <button
            v-tooltip="tab.tooltip"
            class="tw-relative tw-flex tw-h-12 tw-items-center tw-text-xs tw-font-medium tw-uppercase"
            type="button"
            data-test-role="tab"
            @click="() => (valueProxy = tab.key)"
          >
            <span class="tw-hidden tablet:tw-inline-block">
              {{ tab.label }}
            </span>
            <span class="tablet:tw-hidden">
              {{ tab.shortLabel || tab.label }}
            </span>
            <span
              v-if="tab.count && tab.count > 0"
              class="tw-shadow-solid tw-ml-3 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue tw-text-xs tw-text-white"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>
      </template>
    </div>
    <slot />
  </div>
</template>
